import { ChatSession, InvestigationSummary, Finding, MessageWithObservability, ReportBlock } from '../../../types';
import { ConnectionState } from '../../../types/frontend';

// Reduce a session to what the sidebar list needs by dropping the heavy
// collections a full session carries. Applied wherever a (possibly full) session
// enters `sessions` so the list stays lightweight.
//
// Subtractive on purpose: naming what to drop keeps every other field, including
// ones added to the backend model later, which a pick-list would discard.
const toSummary = (s: InvestigationSummary): InvestigationSummary => {
  const { findings, knowledge_items, messages, ...summary } =
    s as InvestigationSummary & Partial<ChatSession>;
  void findings;
  void knowledge_items;
  void messages;
  return summary;
};

export interface ChatState {
  // `currentSession` is the full opened investigation; `sessions` is the sidebar
  // list, which carries lightweight summaries (no findings/knowledge_items).
  currentSession: ChatSession | null;
  sessions: InvestigationSummary[];

  // Connection & streaming state
  status: 'idle' | 'loading' | 'streaming' | 'error';
  error: string | null;
  connectionState: ConnectionState;

  // Streaming data
  streamingMessage: string;
  agentActivity: string | null;

  // Progress tracking
  operationStartTime: number | null;
  lastEventTimestamp: number | null;

  // Timeout detection
  timeoutWarning: boolean;

  pendingMessageContent: string | null;

  // Context occupancy after the last completed turn. maxContextTokens is null
  // when the agent's spec declares no window, which means no percentage.
  contextUsage: { contextTokens: number; maxContextTokens: number | null } | null;

  // Inline blocks emitted by the agent during the in-flight turn, flushed onto
  // the assistant message when the turn completes.
  streamingBlocks: ReportBlock[];

  // Message queue: messages typed while agent is streaming
  messageQueue: string[];
}

export type ChatAction =
  | { type: 'SESSIONS_LOADED'; sessions: InvestigationSummary[] }
  | { type: 'SESSION_SELECTED'; session: ChatSession }
  | { type: 'SESSION_CREATED'; session: ChatSession }
  | { type: 'SESSION_CLEARED' }
  | { type: 'SESSION_UPDATED'; session: ChatSession }
  | { type: 'SESSION_DELETED'; sessionId: string }
  | { type: 'SENDING_MESSAGE'; content: string }
  | { type: 'MESSAGE_SENT' }
  | { type: 'STREAMING_START' }
  | { type: 'CHUNK_RECEIVED'; text: string }
  | { type: 'THINKING'; message: string }
  | { type: 'STREAMING_COMPLETE'; assistantMessage: MessageWithObservability }
  | { type: 'CLEAR_STREAMING' }
  | { type: 'ERROR'; error: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CONNECTION_STATE_CHANGED'; connectionState: ConnectionState }
  | { type: 'TIMEOUT_WARNING'; show: boolean }
  | { type: 'CONVERSATION_CLEARED' }
  | { type: 'FINDING_ADDED'; finding: Finding }
  | { type: 'BLOCK_ADDED'; block: ReportBlock; investigation_id: string }
  | { type: 'MESSAGE_QUEUED'; content: string }
  | { type: 'DEQUEUE_MESSAGE' };

export const initialChatState: ChatState = {
  currentSession: null,
  sessions: [],
  status: 'idle',
  error: null,
  connectionState: 'disconnected',
  streamingMessage: '',
  agentActivity: null,
  operationStartTime: null,
  lastEventTimestamp: null,
  timeoutWarning: false,
  pendingMessageContent: null,
  contextUsage: null,
  streamingBlocks: [],
  messageQueue: [],
};

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SESSIONS_LOADED':
      return {
        ...state,
        sessions: action.sessions.map(toSummary),
        status: 'idle',
        error: null,
      };

    case 'SESSION_SELECTED':
      return {
        ...state,
        currentSession: action.session,
        // Ensure the selected session is in the list — a deep-linked session may
        // be absent from the (paginated/filtered) list, and it must still be
        // selectable and visible in the sidebar rather than silently dropped.
        sessions: state.sessions.some(s => s.id === action.session.id)
          ? state.sessions
          : [toSummary(action.session), ...state.sessions],
        status: 'idle',
        error: null,
        streamingMessage: '',
        agentActivity: null,
        contextUsage: null,
        streamingBlocks: [],
      };

    case 'SESSION_CREATED':
      return {
        ...state,
        currentSession: action.session,
        sessions: [toSummary(action.session), ...state.sessions],
        status: 'idle',
        error: null,
      };

    case 'SESSION_CLEARED':
      return {
        ...state,
        currentSession: null,
        streamingMessage: '',
        agentActivity: null,
        error: null,
      };

    case 'SESSION_UPDATED':
      return {
        ...state,
        sessions: state.sessions.map(s =>
          s.id === action.session.id ? toSummary({ ...s, ...action.session }) : s
        ),
        currentSession:
          state.currentSession?.id === action.session.id
            ? { ...state.currentSession, ...action.session }
            : state.currentSession,
      };

    case 'SESSION_DELETED':
      return {
        ...state,
        sessions: state.sessions.filter(s => s.id !== action.sessionId),
        currentSession:
          state.currentSession?.id === action.sessionId
            ? null
            : state.currentSession,
      };

    case 'SENDING_MESSAGE':
      return {
        ...state,
        pendingMessageContent: action.content,
        status: 'loading',
        agentActivity: 'Sending message...',
        error: null,
        operationStartTime: Date.now(),
        lastEventTimestamp: null,
        timeoutWarning: false,
      };

    case 'MESSAGE_SENT': {
      if (!state.currentSession) return state;

      // The message list lives in the query cache (ADR-0049); sendMessage appends
      // the user message there. This transition only advances streaming status.
      return {
        ...state,
        status: 'loading',
        pendingMessageContent: null,
        agentActivity: 'Thinking...',
      };
    }

    case 'STREAMING_START':
      return {
        ...state,
        status: 'streaming',
      };

    case 'CHUNK_RECEIVED':
      return {
        ...state,
        status: 'streaming' as const,
        streamingMessage: state.streamingMessage + action.text,
        lastEventTimestamp: Date.now(),
        timeoutWarning: false, // Clear timeout on new event
      };

    case 'THINKING':
      return {
        ...state,
        agentActivity: action.message,
        lastEventTimestamp: Date.now(),
        timeoutWarning: false, // Clear timeout on new event
      };

    case 'STREAMING_COMPLETE': {
      if (!state.currentSession) return state;

      // The finalized assistant message is appended to the query cache by the
      // websocket hook (which owns the streaming refs). Here we only close out the
      // in-flight turn: reset streaming buffers and record context-window usage.
      const msgTokens = action.assistantMessage.token_usage;

      return {
        ...state,
        status: 'idle',
        streamingMessage: '',
        agentActivity: null,
        error: null,
        operationStartTime: null,
        lastEventTimestamp: null,
        timeoutWarning: false,
        streamingBlocks: [],
        contextUsage: msgTokens?.context_tokens != null ? {
          contextTokens: msgTokens.context_tokens,
          maxContextTokens: msgTokens.max_context_tokens ?? null,
        } : state.contextUsage,
      };
    }

    case 'CLEAR_STREAMING':
      return {
        ...state,
        status: 'idle',
        streamingMessage: '',
        agentActivity: null,
        operationStartTime: null,
        lastEventTimestamp: null,
        timeoutWarning: false,
        streamingBlocks: [],
        messageQueue: [],
      };

    case 'ERROR':
      return {
        ...state,
        status: 'error',
        error: action.error,
        streamingMessage: '',
        agentActivity: null,
        operationStartTime: null,
        lastEventTimestamp: null,
        timeoutWarning: false,
        streamingBlocks: [],
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'CONNECTION_STATE_CHANGED':
      return {
        ...state,
        connectionState: action.connectionState,
      };

    case 'TIMEOUT_WARNING':
      return {
        ...state,
        timeoutWarning: action.show,
      };

    case 'CONVERSATION_CLEARED': {
      if (!state.currentSession) return state;
      // The message list is emptied in the query cache by clearConversation; this
      // only resets the in-flight streaming/context state.
      return {
        ...state,
        streamingMessage: '',
        agentActivity: null,
        contextUsage: null,
        status: 'idle',
        error: null,
      };
    }

    case 'FINDING_ADDED': {
      if (!state.currentSession) return state;
      const existingFindings = state.currentSession.findings || [];
      if (existingFindings.some(f => f.id === action.finding.id)) return state;
      const updatedSession = {
        ...state.currentSession,
        findings: [...existingFindings, action.finding],
      };
      return {
        ...state,
        currentSession: updatedSession,
        sessions: state.sessions.map(s =>
          s.id === state.currentSession?.id ? toSummary(updatedSession) : s
        ),
      };
    }

    case 'BLOCK_ADDED': {
      if (!state.currentSession || state.currentSession.id !== action.investigation_id) {
        return state;
      }
      return {
        ...state,
        streamingBlocks: [...state.streamingBlocks, action.block],
      };
    }

    case 'MESSAGE_QUEUED':
      return {
        ...state,
        messageQueue: [...state.messageQueue, action.content],
      };

    case 'DEQUEUE_MESSAGE':
      return {
        ...state,
        messageQueue: state.messageQueue.slice(1),
      };

    default:
      return state;
  }
}
